import { uniqueId } from 'lodash';
import { GiftedChat } from 'react-native-gifted-chat';
import apis from '@api/api';
import profileSchema from '@components/modals/TutorialModal/utils/onboardSchema';
import onboardSchema from '@components/modals/TutorialModal/utils/onboardSchema';
import { UPDATE_USER, COMPLETE_ONBOARDING } from '@reduxLocal/actions/types';

export interface OnboardingHandlersConfig {
  dispatch: any;
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setOnboardingStage: (stage: 'profile' | 'context' | 'models' | null) => void;
  setSavedProfileData: (data: Record<string, any>) => void;
  setProfileFormCompleted: (completed: boolean) => void;
  profileFormCompleted: boolean;
  savedProfileData: Record<string, any>;
  sendMessageSequentially: (messages: any[], delay?: number) => Promise<void>;
  globalUser: any;
  userData: any;
}

/**
 * Handle profile form submission (Stage 1)
 */
export const handleProfileFormSubmit = async (
  values: Record<string, any>,
  config: OnboardingHandlersConfig
) => {
  const {
    dispatch,
    setChatMessages,
    setProfileFormCompleted,
    setSavedProfileData,
    setOnboardingStage,
    sendMessageSequentially,
  } = config;

  try {
    if (!values.name || !values.surname) {
      throw new Error('Name and surname are required');
    }

    const response = await apis.updateUserProfile(values);

    if (response && response.success) {
      const responseData = (response as any).data;
      if (responseData?.changes) {
        const updatedUserData: Record<string, any> = {};
        Object.keys(responseData.changes).forEach((key) => {
          const change = responseData.changes[key];
          updatedUserData[key] = change?.new !== undefined ? change.new : change;
        });
        dispatch({ type: UPDATE_USER, payload: updatedUserData });
      } else if (responseData) {
        dispatch({ type: UPDATE_USER, payload: responseData });
      } else {
        dispatch({ type: UPDATE_USER, payload: values });
      }

      setProfileFormCompleted(true);
      setSavedProfileData(values);

      const successMessageId = uniqueId();
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: successMessageId,
            text: "Great! Now let's learn more about your business and preferences.",
            createdAt: new Date(),
            system: true,
            received: true,
          },
        ])
      );

      setTimeout(() => {
        const contextMessageId = uniqueId();
        setChatMessages((prevMessages) =>
          GiftedChat.append(prevMessages, [
            {
              _id: contextMessageId,
              text: 'Please fill out the following information about your business and preferences:',
              createdAt: new Date(),
              system: true,
              showProfileForm: false,
              showOnboardingForm: true,
              formSchema: onboardSchema,
              formSubmitted: false,
              formValues: {},
              received: true,
            },
          ])
        );
        setOnboardingStage('context');
      }, 500);
    } else {
      throw new Error('Failed to update profile');
    }
  } catch (error) {
    console.error('❌ [EasyModeScreen] Error submitting profile form:', error);
    setProfileFormCompleted(false);

    const errorMessageId = uniqueId();
    setChatMessages((prevMessages) =>
      GiftedChat.append(prevMessages, [
        {
          _id: errorMessageId,
          text: 'Sorry, there was an error saving your profile information. Please try again.',
          createdAt: new Date(),
          system: true,
          received: true,
        },
      ])
    );
    throw error;
  }
};

/**
 * Handle business/context form submission (Stage 2)
 */
export const handleBusinessFormSubmit = async (
  values: Record<string, any>,
  config: OnboardingHandlersConfig
) => {
  const {
    dispatch,
    setChatMessages,
    setOnboardingStage,
    setSavedProfileData,
    profileFormCompleted,
    savedProfileData,
  } = config;

  const hasProfileData = Object.keys(savedProfileData).length > 0;
  const isProfileComplete = profileFormCompleted || hasProfileData;

  if (!isProfileComplete) {
    setChatMessages((prevMessages) =>
      GiftedChat.append(prevMessages, [
        {
          _id: uniqueId(),
          text: 'Please complete your profile information first before filling out business details.',
          createdAt: new Date(),
          system: true,
          received: true,
        },
      ])
    );
    throw new Error('Profile form must be completed first');
  }

  try {
    const contextFields = [
      'industry',
      'company_size',
      'experience_level',
      'business_type',
      'location',
      'preferred_language',
      'key_interests',
      'technical_skills',
    ];

    const contextData: Record<string, any> = {};
    const profileData: Record<string, any> = {};

    Object.entries(values).forEach(([key, value]) => {
      if (contextFields.includes(key)) {
        if (key === 'key_interests' || key === 'technical_skills') {
          contextData[key] =
            typeof value === 'string'
              ? value.split(',').map((item) => item.trim()).filter((item) => item.length > 0)
              : Array.isArray(value)
              ? value
              : [];
        } else {
          contextData[key] = value;
        }
      } else {
        profileData[key] = value;
      }
    });

    const mergedProfileData = { ...savedProfileData, ...profileData };
    const payload: any = { ...mergedProfileData };
    if (Object.keys(contextData).length > 0) {
      payload.context = contextData;
    }

    console.log('📡 [EasyModeScreen] Calling updateUserProfile API with payload:', payload);
    const response = await apis.updateUserProfile(payload);
    console.log('📥 [EasyModeScreen] API response:', response);

    if (response && response.success) {
      const responseData = (response as any).data;
      if (responseData?.changes) {
        const updatedUserData: Record<string, any> = {};
        Object.keys(responseData.changes).forEach((key) => {
          const change = responseData.changes[key];
          updatedUserData[key] = change?.new !== undefined ? change.new : change;
        });
        dispatch({ type: UPDATE_USER, payload: updatedUserData });
      } else if (responseData) {
        dispatch({ type: UPDATE_USER, payload: responseData });
      } else {
        dispatch({ type: UPDATE_USER, payload: mergedProfileData });
      }

      const successMessageId = uniqueId();
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
        {
          _id: successMessageId,
          text: 'Great! Your profile has been updated. Now let\'s find the best data models for your business.',
          createdAt: new Date(),
          system: true,
          received: true,
        },
      ])
      );

      // Extract business description from form values
      const businessDescriptionParts: string[] = [];
      if (values.company_name) {
        businessDescriptionParts.push(`Company: ${values.company_name}`);
      }
      if (values.industry) {
        businessDescriptionParts.push(`Industry: ${values.industry}`);
      }
      if (values.business_type) {
        businessDescriptionParts.push(`Business type: ${values.business_type}`);
      }
      if (values.current_challenges) {
        businessDescriptionParts.push(`Challenges: ${values.current_challenges}`);
      }
      if (values.saas_goals) {
        businessDescriptionParts.push(`Goals: ${values.saas_goals}`);
      }
      
      const businessDescription = businessDescriptionParts.length > 0
        ? businessDescriptionParts.join('. ')
        : `${values.industry || 'Business'} in ${values.location || 'various locations'}`;

      // Extract tags from form values
      const tags: string[] = [];
      if (values.industry) tags.push(values.industry);
      if (values.business_type) tags.push(values.business_type);
      if (values.key_interests) {
        const interests = typeof values.key_interests === 'string'
          ? values.key_interests.split(',').map((i: string) => i.trim())
          : Array.isArray(values.key_interests) ? values.key_interests : [];
        tags.push(...interests);
      }
      if (values.technical_skills) {
        const skills = typeof values.technical_skills === 'string'
          ? values.technical_skills.split(',').map((s: string) => s.trim())
          : Array.isArray(values.technical_skills) ? values.technical_skills : [];
        tags.push(...skills);
      }

      // Generate example models as fallback
      const generateExampleModels = (): any[] => {
        const exampleModels = [
          {
            _id: `example_crm_${Date.now()}`,
            schemaTitle: 'Customer Relationship Management',
            dataTags: ['CRM', 'Customer', 'Contact'],
            description: 'A basic CRM model to manage customer contacts and relationships',
            schemaContent: [
              {
                title: 'name',
                type: 'text',
                validation: { required: true, minCharacters: 2 },
                display: { label: 'Name', placeholder: 'Enter name' }
              },
              {
                title: 'email',
                type: 'email',
                validation: { required: true },
                display: { label: 'Email', placeholder: 'Enter email address' }
              },
              {
                title: 'phone',
                type: 'phone',
                validation: { required: false },
                display: { label: 'Phone', placeholder: 'Enter phone number' }
              },
              {
                title: 'company',
                type: 'text',
                validation: { required: false },
                display: { label: 'Company', placeholder: 'Enter company name' }
              },
              {
                title: 'notes',
                type: 'text',
                validation: { required: false, multiline: true },
                display: { label: 'Notes', placeholder: 'Additional notes' }
              }
            ],
            isSuggested: true,
            isExample: true,
            modelType: 'CRM'
          },
          {
            _id: `example_product_${Date.now()}`,
            schemaTitle: 'Product Management',
            dataTags: ['Product', 'Inventory', 'Catalog'],
            description: 'A basic product management model to track products and inventory',
            schemaContent: [
              {
                title: 'product_name',
                type: 'text',
                validation: { required: true },
                display: { label: 'Product Name', placeholder: 'Enter product name' }
              },
              {
                title: 'description',
                type: 'text',
                validation: { required: false, multiline: true },
                display: { label: 'Description', placeholder: 'Enter product description' }
              },
              {
                title: 'price',
                type: 'number',
                validation: { required: true, min: 0 },
                display: { label: 'Price', placeholder: 'Enter price' }
              },
              {
                title: 'quantity',
                type: 'number',
                validation: { required: true, min: 0 },
                display: { label: 'Quantity', placeholder: 'Enter quantity' }
              },
              {
                title: 'category',
                type: 'text',
                validation: { required: false },
                display: { label: 'Category', placeholder: 'Enter category' }
              }
            ],
            isSuggested: true,
            isExample: true,
            modelType: 'Product Management'
          },
          {
            _id: `example_general_${Date.now()}`,
            schemaTitle: 'General Business Data',
            dataTags: ['General', 'Business', 'Data'],
            description: 'A general-purpose data model for various business needs',
            schemaContent: [
              {
                title: 'title',
                type: 'text',
                validation: { required: true },
                display: { label: 'Title', placeholder: 'Enter title' }
              },
              {
                title: 'description',
                type: 'text',
                validation: { required: false, multiline: true },
                display: { label: 'Description', placeholder: 'Enter description' }
              },
              {
                title: 'status',
                type: 'select',
                validation: { required: false },
                display: { 
                  label: 'Status', 
                  placeholder: 'Select status',
                  options: ['Active', 'Inactive', 'Pending']
                }
              },
              {
                title: 'date',
                type: 'date',
                validation: { required: false },
                display: { label: 'Date', placeholder: 'Select date' }
              }
            ],
            isSuggested: true,
            isExample: true,
            modelType: 'General Business Data'
          }
        ];
        return exampleModels;
      };

      // Call identifyOnboardingModels API with retry logic
      let models: any[] = [];
      let responseSuccess = false;
      let lastError: any = null;
      const maxRetries = 2;
      let retryCount = 0;

      while (retryCount <= maxRetries) {
        try {
          const userId = config.globalUser?._id || config.globalUser?.id;
          const companyId = config.userData?.companyId || config.userData?.company?._id;

          console.log(`🔍 [EasyModeScreen] Detecting relevant models... (attempt ${retryCount + 1}/${maxRetries + 1})`, {
            businessDescription,
            tags,
            userId,
            companyId,
          });

          const modelsResponse = await apis.identifyOnboardingModels(
            businessDescription,
            tags,
            userId,
            companyId
          );

          console.log('📥 [EasyModeScreen] Models API response:', modelsResponse);
          console.log('📥 [EasyModeScreen] Models API response type:', typeof modelsResponse);
          console.log('📥 [EasyModeScreen] Models API response keys:', modelsResponse ? Object.keys(modelsResponse) : 'null');

          // Handle different response structures
          // Check in order from most specific to least specific
          // 1. Check for direct structure: { success: true, data: [...] } (most common)
          if (modelsResponse?.success !== undefined && Array.isArray(modelsResponse?.data)) {
            responseSuccess = modelsResponse.success === true;
            models = modelsResponse.data;
            console.log('📦 [EasyModeScreen] Parsed models from direct structure:', models.length);
          }
          // 2. Check if response is wrapped in a data property: { data: { success: true, data: [...] } }
          else if (modelsResponse?.data?.success !== undefined && Array.isArray(modelsResponse?.data?.data)) {
            responseSuccess = modelsResponse.data.success === true;
            models = modelsResponse.data.data;
            console.log('📦 [EasyModeScreen] Parsed models from nested data structure:', models.length);
          }
          // 3. Check if data is directly an array: { data: [...] }
          else if (Array.isArray(modelsResponse?.data)) {
            models = modelsResponse.data;
            responseSuccess = models.length > 0;
            console.log('📦 [EasyModeScreen] Parsed models from data array:', models.length);
          }
          // 4. Check if response is directly an array
          else if (Array.isArray(modelsResponse)) {
            models = modelsResponse;
            responseSuccess = models.length > 0;
            console.log('📦 [EasyModeScreen] Parsed models from array structure:', models.length);
          }
          // 5. Last resort: try to extract from any nested structure
          else if (modelsResponse?.data && typeof modelsResponse.data === 'object') {
            // Try to find array in nested structure
            const findArray = (obj: any): any[] => {
              if (Array.isArray(obj)) return obj;
              if (typeof obj !== 'object' || obj === null) return [];
              for (const key in obj) {
                if (Array.isArray(obj[key])) return obj[key];
                const nested = findArray(obj[key]);
                if (nested.length > 0) return nested;
              }
              return [];
            };
            models = findArray(modelsResponse.data);
            responseSuccess = models.length > 0;
            console.log('📦 [EasyModeScreen] Parsed models from nested search:', models.length);
          }

          // If we got models, break out of retry loop
          if (models.length > 0) {
            break;
          }

          // If no models and not last retry, continue to next retry
          if (retryCount < maxRetries) {
            retryCount++;
            console.log(`⚠️ [EasyModeScreen] No models found, retrying... (${retryCount}/${maxRetries})`);
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }

        } catch (modelsError: any) {
          lastError = modelsError;
          console.error(`❌ [EasyModeScreen] Error detecting models (attempt ${retryCount + 1}):`, modelsError);
          console.error('❌ [EasyModeScreen] Error details:', {
            message: modelsError?.message,
            stack: modelsError?.stack,
            response: modelsError?.response?.data,
            status: modelsError?.response?.status,
          });

          // If not last retry, continue to next retry
          if (retryCount < maxRetries) {
            retryCount++;
            console.log(`🔄 [EasyModeScreen] Retrying after error... (${retryCount}/${maxRetries})`);
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }
        }

        // If we reach here and no models, break to use fallback
        break;
      }

      // Validate models array
      if (models.length > 0) {
        // Filter out invalid models (must have at least schemaTitle or title)
        const validModels = models.filter((model: any) => {
          const hasTitle = !!(model.schemaTitle || model.title);
          if (!hasTitle) {
            console.warn('⚠️ [EasyModeScreen] Invalid model found (no title):', model);
          }
          return hasTitle;
        });

        if (validModels.length > 0) {
          console.log('✅ [EasyModeScreen] Found', validModels.length, 'valid model(s)');
          
          // Create message with suggested models
          const modelsMessageId = uniqueId();
          setChatMessages((prevMessages) =>
            GiftedChat.append(prevMessages, [
              {
                _id: modelsMessageId,
                text: validModels.length === 1 
                  ? 'Based on your business information, I\'ve found a relevant data model that might be useful for you:'
                  : `Based on your business information, I've found ${validModels.length} relevant data models that might be useful for you:`,
                createdAt: new Date(),
                system: true,
                suggestedModels: validModels,
                received: true,
                canRetry: true, // Allow retry if user wants to reload
                retryFunction: async () => {
                  // Retry fetching models
                  const retryMessageId = uniqueId();
                  setChatMessages((prevMessages) =>
                    GiftedChat.append(prevMessages, [
                      {
                        _id: retryMessageId,
                        text: 'Reloading data models...',
                        createdAt: new Date(),
                        system: true,
                        received: true,
                      },
                    ])
                  );
                  
                  try {
                    const userId = config.globalUser?._id || config.globalUser?.id;
                    const companyId = config.userData?.companyId || config.userData?.company?._id;
                    const retryResponse = await apis.identifyOnboardingModels(
                      businessDescription,
                      tags,
                      userId,
                      companyId
                    );
                    
                    // Parse response (same logic as above)
                    let retryModels: any[] = [];
                    if (retryResponse?.success !== undefined && Array.isArray(retryResponse?.data)) {
                      retryModels = retryResponse.data;
                    } else if (Array.isArray(retryResponse?.data)) {
                      retryModels = retryResponse.data;
                    } else if (Array.isArray(retryResponse)) {
                      retryModels = retryResponse;
                    }
                    
                    // Remove retry message
                    setChatMessages((prevMessages) =>
                      prevMessages.filter((msg) => msg._id !== retryMessageId)
                    );
                    
                    if (retryModels.length > 0) {
                      const validRetryModels = retryModels.filter((model: any) => 
                        !!(model.schemaTitle || model.title)
                      );
                      
                      if (validRetryModels.length > 0) {
                        // Update the models message with new models
                        setChatMessages((prevMessages) =>
                          prevMessages.map((msg) => {
                            if (msg._id === modelsMessageId) {
                              return {
                                ...msg,
                                suggestedModels: validRetryModels,
                                text: validRetryModels.length === 1 
                                  ? 'Based on your business information, I\'ve found a relevant data model that might be useful for you:'
                                  : `Based on your business information, I've found ${validRetryModels.length} relevant data models that might be useful for you:`,
                              };
                            }
                            return msg;
                          })
                        );
                      }
                    }
                  } catch (retryError) {
                    console.error('❌ [EasyModeScreen] Error retrying model fetch:', retryError);
                    setChatMessages((prevMessages) =>
                      prevMessages.map((msg) => {
                        if (msg._id === retryMessageId) {
                          return {
                            ...msg,
                            text: 'Failed to reload models. You can select from examples or continue without models.',
                          };
                        }
                        return msg;
                      })
                    );
                  }
                },
              },
            ])
          );

          setOnboardingStage('models');
          console.log('✅ [EasyModeScreen] Model selection stage started with', validModels.length, 'model(s)');
        } else {
          // All models were invalid - use example models as fallback
          console.warn('⚠️ [EasyModeScreen] All models were invalid, using example models as fallback');
          const exampleModels = generateExampleModels();
          const modelsMessageId = uniqueId();
          setChatMessages((prevMessages) =>
            GiftedChat.append(prevMessages, [
              {
                _id: modelsMessageId,
                text: 'I couldn\'t find specific models for your business, but here are some example models you can use:',
                createdAt: new Date(),
                system: true,
                suggestedModels: exampleModels,
                received: true,
                isExampleFallback: true,
              },
            ])
          );
          setOnboardingStage('models');
        }
      } else {
        // No models found or API failed - use example models as fallback
        console.log('⚠️ [EasyModeScreen] No models found or API failed, using example models as fallback');
        const exampleModels = generateExampleModels();
        const fallbackMessageId = uniqueId();
        
        let errorText = 'I couldn\'t find specific models for your business right now.';
        if (lastError) {
          errorText += ' Here are some example models you can use instead:';
        } else {
          errorText += ' Here are some example models you can use:';
        }
        
        setChatMessages((prevMessages) =>
          GiftedChat.append(prevMessages, [
            {
              _id: fallbackMessageId,
              text: errorText,
              createdAt: new Date(),
              system: true,
              suggestedModels: exampleModels,
              received: true,
              isExampleFallback: true,
              canRetry: true,
              retryFunction: async () => {
                // Retry fetching models
                const retryMessageId = uniqueId();
                setChatMessages((prevMessages) =>
                  GiftedChat.append(prevMessages, [
                    {
                      _id: retryMessageId,
                      text: 'Reloading data models...',
                      createdAt: new Date(),
                      system: true,
                      received: true,
                    },
                  ])
                );
                
                try {
                  const userId = config.globalUser?._id || config.globalUser?.id;
                  const companyId = config.userData?.companyId || config.userData?.company?._id;
                  const retryResponse = await apis.identifyOnboardingModels(
                    businessDescription,
                    tags,
                    userId,
                    companyId
                  );
                  
                  // Parse response
                  let retryModels: any[] = [];
                  if (retryResponse?.success !== undefined && Array.isArray(retryResponse?.data)) {
                    retryModels = retryResponse.data;
                  } else if (Array.isArray(retryResponse?.data)) {
                    retryModels = retryResponse.data;
                  } else if (Array.isArray(retryResponse)) {
                    retryModels = retryResponse;
                  }
                  
                  // Remove retry message
                  setChatMessages((prevMessages) =>
                    prevMessages.filter((msg) => msg._id !== retryMessageId)
                  );
                  
                  if (retryModels.length > 0) {
                    const validRetryModels = retryModels.filter((model: any) => 
                      !!(model.schemaTitle || model.title)
                    );
                    
                    if (validRetryModels.length > 0) {
                      // Replace example models with fetched models
                      setChatMessages((prevMessages) =>
                        prevMessages.map((msg) => {
                          if (msg._id === fallbackMessageId) {
                            return {
                              ...msg,
                              suggestedModels: validRetryModels,
                              text: validRetryModels.length === 1 
                                ? 'Based on your business information, I\'ve found a relevant data model that might be useful for you:'
                                : `Based on your business information, I've found ${validRetryModels.length} relevant data models that might be useful for you:`,
                              isExampleFallback: false,
                            };
                          }
                          return msg;
                        })
                      );
                    }
                  } else {
                    // Still no models, keep examples
                    setChatMessages((prevMessages) =>
                      prevMessages.map((msg) => {
                        if (msg._id === retryMessageId) {
                          return {
                            ...msg,
                            text: 'Still couldn\'t find specific models. The example models are still available for you to use.',
                          };
                        }
                        return msg;
                      })
                    );
                  }
                } catch (retryError) {
                  console.error('❌ [EasyModeScreen] Error retrying model fetch:', retryError);
                  setChatMessages((prevMessages) =>
                    prevMessages.map((msg) => {
                      if (msg._id === retryMessageId) {
                        return {
                          ...msg,
                          text: 'Failed to reload models. The example models are still available for you to use.',
                        };
                      }
                      return msg;
                    })
                  );
                }
              },
            },
          ])
        );
        setOnboardingStage('models');
      }
    } else {
      throw new Error('Failed to update profile');
    }
  } catch (error) {
    console.error('❌ [EasyModeScreen] Error submitting onboarding form:', error);
    const errorMessage = error?.message
      ? `Sorry, there was an error saving your profile: ${error.message}. Please try again.`
      : 'Sorry, there was an error saving your profile. Please try again.';

    const errorMessageId = uniqueId();
    setChatMessages((prevMessages) =>
      GiftedChat.append(prevMessages, [
        {
          _id: errorMessageId,
          text: errorMessage,
          createdAt: new Date(),
          system: true,
          received: true,
        },
      ])
    );
    throw error;
  }
};

